/**
 * CuratorView - HyDE-Powered Narrative Search
 *
 * Instead of raw keyword/embedding search, the Curator synthesizes an
 * "anchor passage" — a hypothetical document that embeds into the right
 * region of latent space, finding narrative quality not just keywords.
 *
 * Three sections:
 * - Chat: user describes what they're looking for
 * - Anchor: editable synthesized passage (the search signal)
 * - Results: content ranked by cosine similarity to anchor embedding
 *
 * Enhanced with FindBuffer navigation: each search creates a navigable
 * "find buffer" that can be revisited, and results can be expanded in-modal
 * with text selection for fine-tuned "Find Similar" searches.
 *
 * @module @humanizer/studio/components/archive/CuratorView
 */

import React, { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import { useCuratorApi } from '../../hooks/useCuratorApi';
import type { CuratorSearchResult } from '../../types/electron';
import '../../styles/curator-view.css';
import { createLogger } from '../../utils/debug';

const log = createLogger('CuratorView');

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

/** A single search layer — one query + anchor + results */
export interface FindBuffer {
  id: string;
  query: string;
  anchorPassage: string;
  results: CuratorSearchResult[];
  timestamp: number;
  parentFindBufferId?: string;
  savedBufferId?: string;
}

/** Persisted curator session state — survives tab switches via parent ref */
export interface CuratorSessionState {
  messages: ChatMessage[];
  findBuffers: FindBuffer[];
  activeFindBufferIndex: number;
  // Legacy flat fields for backward compat migration
  anchorPassage?: string;
  originalQuery?: string;
  results?: CuratorSearchResult[];
  resultCount?: number;
}

export interface CuratorViewProps {
  archiveId?: string;
  onSelectContent?: (nodeId: string) => void;
  /** Open a node in the workspace (loads full thread with message navigation) */
  onOpenNode?: (nodeId: string) => void;
  /** Add item to active harvest */
  onAddToHarvest?: (item: { content: string; source?: string }) => void;
  /** Add result to workspace card stack (D2) */
  onAddToStack?: (nodeId: string, text: string, sourceType?: string) => void;
  /** Pre-fill and auto-trigger search on mount (D3) */
  initialQuery?: string | null;
  /** Ref for persisting state across tab switches */
  sessionStateRef?: MutableRefObject<CuratorSessionState | undefined>;
  /** Active project ID for auto-saving find buffers (M4) */
  activeProjectId?: string;
  /** Reopen a previous search from ProjectBrowser (M5) */
  initialFindBuffer?: FindBuffer;
  /** Called after a find buffer is auto-saved (Phase 3) */
  onSearchSaved?: (fb: FindBuffer) => void;
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

/** Migrate legacy flat CuratorSessionState to findBuffers format */
function migrateSessionState(saved: CuratorSessionState): CuratorSessionState {
  if (saved.findBuffers && saved.findBuffers.length > 0) return saved;
  // Legacy format: flat anchorPassage/results/originalQuery/resultCount
  if (saved.anchorPassage || (saved.results && saved.results.length > 0)) {
    const legacyBuffer: FindBuffer = {
      id: crypto.randomUUID(),
      query: saved.originalQuery ?? '',
      anchorPassage: saved.anchorPassage ?? '',
      results: saved.results ?? [],
      timestamp: Date.now(),
    };
    return {
      messages: saved.messages,
      findBuffers: [legacyBuffer],
      activeFindBufferIndex: 0,
    };
  }
  return { ...saved, findBuffers: [], activeFindBufferIndex: 0 };
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

const INITIAL_MESSAGES: ChatMessage[] = [
  { role: 'system', content: 'Describe what you\'re looking for. I\'ll synthesize an anchor passage to find it.' },
];

export function CuratorView({ archiveId, onSelectContent, onOpenNode, onAddToHarvest, onAddToStack, initialQuery, sessionStateRef, activeProjectId, initialFindBuffer, onSearchSaved }: CuratorViewProps) {
  const curator = useCuratorApi();

  // Init from persisted state or defaults (with migration)
  const saved = sessionStateRef?.current ? migrateSessionState(sessionStateRef.current) : undefined;

  // Chat state
  const [messages, setMessages] = useState<ChatMessage[]>(saved?.messages ?? INITIAL_MESSAGES);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // FindBuffer state (M2)
  const [findBuffers, setFindBuffers] = useState<FindBuffer[]>(saved?.findBuffers ?? []);
  const [activeFindBufferIndex, setActiveFindBufferIndex] = useState(saved?.activeFindBufferIndex ?? 0);

  // Expanded result state (M1)
  const [expandedNodeId, setExpandedNodeId] = useState<string | null>(null);
  const [expandedSelectedText, setExpandedSelectedText] = useState<string | null>(null);

  // Nav dropdown state (M3)
  const [navDropdownSide, setNavDropdownSide] = useState<'back' | 'forward' | null>(null);
  const navDropdownTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navDropdownRef = useRef<HTMLDivElement>(null);

  // Derive active find buffer
  const activeFindBuffer = findBuffers[activeFindBufferIndex] ?? null;
  const results = activeFindBuffer?.results ?? [];
  const anchorPassage = activeFindBuffer?.anchorPassage ?? '';
  const originalQuery = activeFindBuffer?.query ?? '';
  const resultCount = results.length;

  // Write state back to parent ref on changes (survives unmount/remount)
  useEffect(() => {
    if (sessionStateRef) {
      sessionStateRef.current = { messages, findBuffers, activeFindBufferIndex };
    }
  }, [sessionStateRef, messages, findBuffers, activeFindBufferIndex]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // D3: Auto-trigger search when initialQuery is provided and no anchor exists yet
  const initialQueryTriggered = useRef(false);
  useEffect(() => {
    if (initialQuery && !initialQueryTriggered.current && findBuffers.length === 0 && !isLoading) {
      initialQueryTriggered.current = true;
      handleSearch(initialQuery);
    }
  }, [initialQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // M5: Auto-trigger search when initialFindBuffer is provided with empty results
  const initialFindBufferTriggered = useRef(false);
  useEffect(() => {
    if (initialFindBuffer && !initialFindBufferTriggered.current && !isLoading) {
      initialFindBufferTriggered.current = true;
      if (initialFindBuffer.results.length === 0 && initialFindBuffer.anchorPassage) {
        // Reopen: search with saved anchor
        setIsLoading(true);
        setFindBuffers([initialFindBuffer]);
        setActiveFindBufferIndex(0);
        curator.searchWithAnchor(initialFindBuffer.anchorPassage, { limit: 20 })
          .then((searchResults) => {
            setFindBuffers([{ ...initialFindBuffer, results: searchResults }]);
            setMessages(prev => [...prev, {
              role: 'system',
              content: `Reopened search: "${initialFindBuffer.query}". Found ${searchResults.length} results.`,
            }]);
          })
          .catch((err) => {
            const msg = err instanceof Error ? err.message : String(err);
            setMessages(prev => [...prev, { role: 'system', content: `Error reopening: ${msg}` }]);
          })
          .finally(() => setIsLoading(false));
      } else {
        setFindBuffers([initialFindBuffer]);
        setActiveFindBufferIndex(0);
      }
    }
  }, [initialFindBuffer]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close nav dropdown on outside click
  useEffect(() => {
    if (!navDropdownSide) return;
    const handle = (e: MouseEvent) => {
      if (navDropdownRef.current && !navDropdownRef.current.contains(e.target as Node)) {
        setNavDropdownSide(null);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [navDropdownSide]);

  // Escape key: collapse expanded view or close dropdown
  useEffect(() => {
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (expandedNodeId) {
          setExpandedNodeId(null);
          setExpandedSelectedText(null);
        } else if (navDropdownSide) {
          setNavDropdownSide(null);
        }
      }
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [expandedNodeId, navDropdownSide]);

  // ─────────────────────────────────────────────────────────────────────
  // AUTO-SAVE FIND BUFFERS (M4)
  // ─────────────────────────────────────────────────────────────────────

  const autoSaveFindBuffer = useCallback(async (fb: FindBuffer) => {
    if (!activeProjectId || !window.electronBuffer?.createFromText || !window.electronHarvest?.addBufferToProject) return;

    const summaryLines = [
      `# Curator Search`,
      `**Query:** ${fb.query}`,
      `**Anchor:**`,
      `> ${fb.anchorPassage.slice(0, 300)}${fb.anchorPassage.length > 300 ? '...' : ''}`,
      `**Results (${fb.results.length}):**`,
      ...fb.results.slice(0, 10).map((r, i) =>
        `${i + 1}. [${(r.similarity * 100).toFixed(0)}%] ${r.text.slice(0, 80).replace(/\n/g, ' ')}...`
      ),
    ];

    try {
      const buffer = await window.electronBuffer.createFromText(summaryLines.join('\n'), {
        format: 'markdown',
        initialState: 'staged',
        metadata: {
          curatorSearch: true,
          curatorQuery: fb.query,
          curatorAnchor: fb.anchorPassage,
          resultNodeIds: fb.results.map(r => r.nodeId),
          resultCount: fb.results.length,
          parentFindBufferId: fb.parentFindBufferId,
          searchTimestamp: fb.timestamp,
        },
      });
      await window.electronHarvest.addBufferToProject(activeProjectId, buffer.id);
      // Store savedBufferId back on the FindBuffer
      const updatedFb = { ...fb, savedBufferId: buffer.id };
      setFindBuffers(prev => prev.map(f =>
        f.id === fb.id ? updatedFb : f
      ));
      // Notify parent (Phase 3 sidebar refresh)
      onSearchSaved?.(updatedFb);
    } catch (err) {
      log.warn('Failed to auto-save find buffer:', err);
    }
  }, [activeProjectId, onSearchSaved]);

  // ─────────────────────────────────────────────────────────────────────
  // SEARCH FLOW
  // ─────────────────────────────────────────────────────────────────────

  /** Set anchor directly (for editing) — updates the active find buffer */
  const setAnchorPassage = useCallback((newAnchor: string) => {
    setFindBuffers(prev => prev.map((fb, i) =>
      i === activeFindBufferIndex ? { ...fb, anchorPassage: newAnchor } : fb
    ));
  }, [activeFindBufferIndex]);

  const handleSearch = useCallback(async (query: string) => {
    if (!query.trim()) return;

    setIsLoading(true);
    setMessages(prev => [...prev, { role: 'user', content: query }]);

    try {
      // Step 1: Synthesize anchor passage
      const buildContext = messages
        .filter(m => m.role !== 'system')
        .map(m => `${m.role}: ${m.content}`)
        .join('\n');

      const { anchorPassage: anchor, durationMs } = await curator.synthesize(query, {
        conversationContext: buildContext || undefined,
      });

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `I've synthesized an anchor passage (${(durationMs / 1000).toFixed(1)}s). Searching your archive...`,
      }]);

      // Step 2: Search with anchor
      const searchResults = await curator.searchWithAnchor(anchor, { limit: 20 });

      // Create new FindBuffer
      const newFB: FindBuffer = {
        id: crypto.randomUUID(),
        query,
        anchorPassage: anchor,
        results: searchResults,
        timestamp: Date.now(),
      };

      setFindBuffers(prev => [...prev, newFB]);
      setActiveFindBufferIndex(prev => {
        const newIdx = findBuffers.length; // Will be appended
        return newIdx;
      });
      setExpandedNodeId(null);

      setMessages(prev => [...prev, {
        role: 'system',
        content: `Found ${searchResults.length} results. You can refine, edit the anchor, or re-embed.`,
      }]);

      // M4: Auto-save
      autoSaveFindBuffer(newFB);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setMessages(prev => [...prev, {
        role: 'system',
        content: `Error: ${msg}`,
      }]);
    } finally {
      setIsLoading(false);
      setTimeout(scrollToBottom, 100);
    }
  }, [curator, messages, scrollToBottom, findBuffers.length, autoSaveFindBuffer]);

  const handleRefine = useCallback(async (refinement: string) => {
    if (!refinement.trim() || !anchorPassage) return;

    setIsLoading(true);
    setMessages(prev => [...prev, { role: 'user', content: refinement }]);

    try {
      const { anchorPassage: newAnchor, durationMs } = await curator.refineQuery(
        originalQuery,
        anchorPassage,
        refinement
      );

      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Refined anchor (${(durationMs / 1000).toFixed(1)}s). Re-searching...`,
      }]);

      const searchResults = await curator.searchWithAnchor(newAnchor, { limit: 20 });

      // Create new FindBuffer for the refinement
      const newFB: FindBuffer = {
        id: crypto.randomUUID(),
        query: `${originalQuery} → ${refinement}`,
        anchorPassage: newAnchor,
        results: searchResults,
        timestamp: Date.now(),
        parentFindBufferId: activeFindBuffer?.id,
      };

      setFindBuffers(prev => [...prev, newFB]);
      setActiveFindBufferIndex(findBuffers.length);
      setExpandedNodeId(null);

      // M4: Auto-save
      autoSaveFindBuffer(newFB);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setMessages(prev => [...prev, {
        role: 'system',
        content: `Error: ${msg}`,
      }]);
    } finally {
      setIsLoading(false);
      setTimeout(scrollToBottom, 100);
    }
  }, [curator, anchorPassage, originalQuery, scrollToBottom, activeFindBuffer?.id, findBuffers.length, autoSaveFindBuffer]);

  // Re-embed edited anchor
  const handleReEmbed = useCallback(async () => {
    if (!anchorPassage.trim()) return;

    setIsLoading(true);
    try {
      const searchResults = await curator.searchWithAnchor(anchorPassage, { limit: 20 });
      // Update results on active find buffer
      setFindBuffers(prev => prev.map((fb, i) =>
        i === activeFindBufferIndex ? { ...fb, results: searchResults } : fb
      ));
      setMessages(prev => [...prev, {
        role: 'system',
        content: `Re-embedded edited anchor. Found ${searchResults.length} results.`,
      }]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setMessages(prev => [...prev, {
        role: 'system',
        content: `Error: ${msg}`,
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [curator, anchorPassage, activeFindBufferIndex]);

  // "Find Similar" — use result text (or selected text from expanded view) as anchor
  const handleFindSimilar = useCallback(async (result: CuratorSearchResult) => {
    const searchText = expandedSelectedText || result.text;
    const queryLabel = expandedSelectedText
      ? `Find selected: "${searchText.slice(0, 60)}..."`
      : `Find similar to: "${result.text.slice(0, 60)}..."`;

    setIsLoading(true);
    setMessages(prev => [...prev, {
      role: 'system',
      content: `${queryLabel} — Searching...`,
    }]);

    try {
      // Use the text directly as anchor (no synthesis needed)
      const searchResults = await curator.searchWithAnchor(searchText.substring(0, 500), { limit: 20 });

      const newFB: FindBuffer = {
        id: crypto.randomUUID(),
        query: queryLabel,
        anchorPassage: searchText.substring(0, 500),
        results: searchResults,
        timestamp: Date.now(),
        parentFindBufferId: activeFindBuffer?.id,
      };

      setFindBuffers(prev => [...prev, newFB]);
      setActiveFindBufferIndex(findBuffers.length);
      setExpandedNodeId(null);
      setExpandedSelectedText(null);

      setMessages(prev => [...prev, {
        role: 'system',
        content: `Found ${searchResults.length} similar results.`,
      }]);

      // M4: Auto-save
      autoSaveFindBuffer(newFB);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setMessages(prev => [...prev, { role: 'system', content: `Error: ${msg}` }]);
    } finally {
      setIsLoading(false);
      setTimeout(scrollToBottom, 100);
    }
  }, [curator, expandedSelectedText, activeFindBuffer?.id, findBuffers.length, scrollToBottom, autoSaveFindBuffer]);

  // Add to harvest
  const handleAddToHarvest = useCallback(async (result: CuratorSearchResult) => {
    if (onAddToHarvest) {
      onAddToHarvest({
        content: result.text,
        source: `curator:${result.nodeId}`,
      });
      setMessages(prev => [...prev, {
        role: 'system',
        content: `Added to harvest: "${result.text.substring(0, 60)}..."`,
      }]);
    } else {
      setMessages(prev => [...prev, {
        role: 'system',
        content: `No active harvest. Open the Harvest tab to create one first.`,
      }]);
    }
  }, [onAddToHarvest]);

  // Handle text selection in expanded result
  const handleExpandedTextSelection = useCallback(() => {
    const selection = window.getSelection();
    const text = selection?.toString().trim() || null;
    setExpandedSelectedText(text && text.length > 0 ? text : null);
  }, []);

  // ─────────────────────────────────────────────────────────────────────
  // NAVIGATION (M3)
  // ─────────────────────────────────────────────────────────────────────

  const canGoBack = activeFindBufferIndex > 0;
  const canGoForward = activeFindBufferIndex < findBuffers.length - 1;

  const navigateTo = useCallback((index: number) => {
    setActiveFindBufferIndex(index);
    setExpandedNodeId(null);
    setExpandedSelectedText(null);
    setNavDropdownSide(null);
  }, []);

  const handleNavHoverStart = useCallback((side: 'back' | 'forward') => {
    if (navDropdownTimer.current) clearTimeout(navDropdownTimer.current);
    navDropdownTimer.current = setTimeout(() => setNavDropdownSide(side), 300);
  }, []);

  const handleNavHoverEnd = useCallback(() => {
    if (navDropdownTimer.current) clearTimeout(navDropdownTimer.current);
  }, []);

  // ─────────────────────────────────────────────────────────────────────
  // SUBMIT
  // ─────────────────────────────────────────────────────────────────────

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const query = inputValue.trim();
    setInputValue('');

    if (anchorPassage) {
      // Subsequent messages = refinements
      handleRefine(query);
    } else {
      // First message = initial search
      handleSearch(query);
    }
  }, [inputValue, isLoading, anchorPassage, handleSearch, handleRefine]);

  // ─────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────

  return (
    <div className="curator-view">
      {/* Chat Messages */}
      <div className="curator-view__chat">
        <div className="curator-view__messages">
          {messages.map((msg, i) => (
            <div key={i} className={`curator-view__message curator-view__message--${msg.role}`}>
              {msg.content}
            </div>
          ))}
          {isLoading && (
            <div className="curator-view__loading">
              <div className="curator-view__spinner" />
              <span>Thinking...</span>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Anchor Display */}
      {anchorPassage && (
        <div className="curator-view__anchor">
          <div className="curator-view__anchor-label">Anchor Passage</div>
          <textarea
            className="curator-view__anchor-text"
            value={anchorPassage}
            onChange={(e) => setAnchorPassage(e.target.value)}
          />
          <div className="curator-view__anchor-actions">
            <button
              className="curator-view__anchor-btn curator-view__anchor-btn--primary"
              onClick={handleReEmbed}
              disabled={isLoading}
            >
              Re-embed
            </button>
            <button
              className="curator-view__anchor-btn"
              onClick={() => {
                // Clear = go back to empty state (don't delete find buffers)
                setFindBuffers(prev => prev.map((fb, i) =>
                  i === activeFindBufferIndex ? { ...fb, anchorPassage: '', results: [] } : fb
                ));
                setMessages(prev => [...prev, {
                  role: 'system',
                  content: 'Anchor cleared. Describe what you\'re looking for.',
                }]);
              }}
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Find Buffer Navigation (M3) */}
      {findBuffers.length > 1 && (
        <div className="curator-view__nav" ref={navDropdownRef}>
          <button
            className="curator-view__nav-btn"
            disabled={!canGoBack}
            onClick={() => navigateTo(activeFindBufferIndex - 1)}
            onMouseEnter={() => canGoBack && handleNavHoverStart('back')}
            onMouseLeave={handleNavHoverEnd}
            aria-label="Previous search"
          >
            &larr;
          </button>
          <span className="curator-view__nav-label">
            Search {activeFindBufferIndex + 1} of {findBuffers.length}
          </span>
          <button
            className="curator-view__nav-btn"
            disabled={!canGoForward}
            onClick={() => navigateTo(activeFindBufferIndex + 1)}
            onMouseEnter={() => canGoForward && handleNavHoverStart('forward')}
            onMouseLeave={handleNavHoverEnd}
            aria-label="Next search"
          >
            &rarr;
          </button>

          {/* Hover dropdown */}
          {navDropdownSide && (
            <div className={`curator-view__nav-dropdown curator-view__nav-dropdown--${navDropdownSide}`}>
              {findBuffers.map((fb, i) => (
                <button
                  key={fb.id}
                  className={`curator-view__nav-dropdown-item${i === activeFindBufferIndex ? ' curator-view__nav-dropdown-item--active' : ''}`}
                  onClick={() => navigateTo(i)}
                >
                  <span className="curator-view__nav-dropdown-query">
                    {fb.query.slice(0, 50)}{fb.query.length > 50 ? '...' : ''}
                  </span>
                  <span className="curator-view__nav-dropdown-count">
                    {fb.results.length} results
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="curator-view__results">
          <div className="curator-view__results-header">
            <span>Results ({resultCount})</span>
          </div>
          {results.map((result) => {
            const isExpanded = expandedNodeId === result.nodeId;

            return (
              <div
                key={result.nodeId}
                className={`curator-view__result${isExpanded ? ' curator-view__result--expanded' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (!isExpanded) {
                    setExpandedNodeId(result.nodeId);
                    setExpandedSelectedText(null);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (!isExpanded) {
                      setExpandedNodeId(result.nodeId);
                      setExpandedSelectedText(null);
                    }
                  }
                }}
              >
                <div className="curator-view__result-meta">
                  <span className="curator-view__result-score">
                    {(result.similarity * 100).toFixed(0)}%
                  </span>
                  <span className="curator-view__result-source">{result.sourceType}</span>
                  {result.sourceCreatedAt && (
                    <span>{new Date(result.sourceCreatedAt).toLocaleDateString()}</span>
                  )}
                </div>

                {isExpanded ? (
                  <>
                    {/* Expanded action bar */}
                    <div className="curator-view__result-action-bar">
                      <button
                        className="curator-view__result-action curator-view__result-action--primary"
                        onClick={(e) => { e.stopPropagation(); handleFindSimilar(result); }}
                        disabled={isLoading}
                        title={expandedSelectedText ? 'Search using selected text' : 'Find passages similar to this result'}
                      >
                        {expandedSelectedText ? 'Find Selected' : 'Find Similar'}
                      </button>
                      {onAddToStack && (
                        <button
                          className="curator-view__result-action"
                          onClick={(e) => { e.stopPropagation(); onAddToStack(result.nodeId, result.text, result.sourceType); }}
                        >
                          + Stack
                        </button>
                      )}
                      <button
                        className="curator-view__result-action"
                        onClick={(e) => { e.stopPropagation(); handleAddToHarvest(result); }}
                      >
                        Harvest
                      </button>
                      <button
                        className="curator-view__result-action"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenNode ? onOpenNode(result.nodeId) : onSelectContent?.(result.nodeId);
                        }}
                      >
                        Open
                      </button>
                      <button
                        className="curator-view__result-action curator-view__result-action--close"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedNodeId(null);
                          setExpandedSelectedText(null);
                        }}
                        aria-label="Collapse"
                      >
                        &times;
                      </button>
                    </div>
                    {/* Full text, scrollable, with text selection */}
                    <div
                      className="curator-view__result-full-text"
                      onMouseUp={(e) => { e.stopPropagation(); handleExpandedTextSelection(); }}
                    >
                      {result.text}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="curator-view__result-text">{result.text}</div>
                    <div className="curator-view__result-actions">
                      <button
                        className="curator-view__result-action"
                        onClick={(e) => { e.stopPropagation(); handleFindSimilar(result); }}
                        disabled={isLoading}
                      >
                        Find Similar
                      </button>
                      <button
                        className="curator-view__result-action"
                        onClick={(e) => { e.stopPropagation(); handleAddToHarvest(result); }}
                      >
                        Add to harvest
                      </button>
                      {onAddToStack && (
                        <button
                          className="curator-view__result-action"
                          onClick={(e) => { e.stopPropagation(); onAddToStack(result.nodeId, result.text, result.sourceType); }}
                        >
                          + Stack
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state */}
      {findBuffers.length === 0 && !isLoading && messages.length <= 1 && (
        <div className="curator-view__empty">
          <p>What are you looking for in your archive?</p>
          <p className="curator-view__empty-hint">
            Try: "moments of clarity" or "conversations about creativity" or "that time I realized..."
          </p>
        </div>
      )}

      {/* Input */}
      <form className="curator-view__input" onSubmit={handleSubmit}>
        <input
          type="text"
          className="curator-view__input-field"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={anchorPassage ? 'Refine your search...' : 'Describe what you\'re looking for...'}
          disabled={isLoading}
        />
        <button
          type="submit"
          className="curator-view__send-btn"
          disabled={isLoading || !inputValue.trim()}
        >
          {anchorPassage ? 'Refine' : 'Search'}
        </button>
      </form>
    </div>
  );
}
